import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCompetitions } from '@/hooks/useCompetitions';
import { useTeams } from '@/hooks/useTeams';
import { useUser } from '@/hooks/useUser';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { usePickemById } from '@/api/query/usePickem';
import { transformPickemToCompetition } from '@/utils/type-transformers';
import { resolveLobbyReturnUrl } from '@low6-pickem/frontend-shared';
import { initiateOAuthLogin } from '@/utils/oauth';

// Components
import AppHeader from '@/components/layout/AppHeader';
import { AppHeaderStyleProvider } from '@low6-pickem/ui';
import { useDefaultHeaderStyle } from '@/hooks/useDefaultHeaderStyle';
import AppFooter from '@/components/layout/AppFooter';
import NavigationTabs from '@/components/layout/NavigationTabs';
import { ScreenLayout } from '@/components/layout/ScreenLayout';
import { CompetitionView } from '@/components/screens/CompetitionView';
import {
  LobbyTab,
  MyPicksTab,
  RecentTab,
  LeaderboardTab,
  FriendsTab,
} from '@/components/screens/main';
import LeagueLeaderboardView from '@/components/leaderboard/LeagueLeaderboardView';
import SportFilter from '@/components/shared/SportFilter';
import type { SportFilterValue } from '@/components/shared/SportFilter';
import WelcomeScreen from '@/components/screens/WelcomeScreen';
import HowToPlayScreen from '@/components/screens/HowToPlayScreen';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { TopPageAd, BelowHeaderAd } from '@/components/ads/HeaderAds';
// import AdDebug from '@/components/ads/AdDebug';
import {
  LeagueCreatedModal,
  PicksSubmittedModal,
  ClearSelectionsModal,
} from '@/components/modals/AppModal';
import ContestDetailsModal from '@/components/modals/ContestDetailsModal';

const ENABLE_MOBILE_CONTAINER = false;

const PickEmApp: React.FC = () => {
  // The contest-details transform resolves its title and prize text per language, so
  // the switcher has to re-run it.
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const [_selectedTeams, setSelectedTeams] = useState<Record<string, string>>({});
  const [_selectedProps, setSelectedProps] = useState<Record<string, 'yes' | 'no'>>({});

  // Modal states
  const [showLeagueCreated, setShowLeagueCreated] = useState(false);
  const [showPicksSubmitted, setShowPicksSubmitted] = useState(false);
  const [showClearSelections, setShowClearSelections] = useState(false);
  const [contestDetailsGameId, setContestDetailsGameId] = useState<string | null>(null);

  // Sport filter state (persists across tab switches)
  const [selectedSport, setSelectedSport] = useState<SportFilterValue>('ALL');

  // Get data from hooks
  const { competitions, isLoading, isFetched } = useCompetitions();
  // Landing/lobby header styling, sourced from the tenant's default-style pickem
  // so the configured banner shows here too (not just on the detail screen) — DKPLAT-446.
  const defaultHeaderStyle = useDefaultHeaderStyle();
  const { getTeamPair: _getTeamPair } = useTeams();
  const { currentUser: _currentUser } = useUser();
  const { isAuthenticated, isLoading: _isAuthLoading } = useAuth();
  const { tenant, isAdsEnabled, adPlacements, isPlacementEnabled, pickemFullNavigation } =
    useTenant();
  const usesClerk = tenant.authProvider === 'clerk';
  const showAdPlaceholders = import.meta.env.MODE === 'development';

  // Fetch pickem details for contest details modal
  const { data: contestDetailsData } = usePickemById(contestDetailsGameId || '');
  // Transform pickem data to competition format
  // Get totalEntries from competitions array (list endpoint has entries, detail endpoint doesn't)
  const contestDetailsCompetition = useMemo(() => {
    if (!contestDetailsData?.pickem) return undefined;
    const transformed = transformPickemToCompetition(contestDetailsData.pickem, i18n.language);
    // Add totalEntries from competitions array if not already set
    if (transformed.totalEntries === undefined && contestDetailsGameId) {
      const comp = competitions.find(c => c.id === contestDetailsGameId);
      if (comp?.totalEntries !== undefined) {
        transformed.totalEntries = comp.totalEntries;
      }
    }
    return transformed;
  }, [contestDetailsData, contestDetailsGameId, competitions, i18n.language]);

  // TOP_PAGE / BELOW_HEADER ad placements are rendered via <TopPageAd /> and
  // <BelowHeaderAd /> wherever AppHeader is shown (here + CompetitionView +
  // HowToPlayScreen); those components own their own gating.
  const showBodyAds =
    !!adPlacements && isPlacementEnabled('BODY') && (isAdsEnabled || showAdPlaceholders);

  // Check if user has seen welcome screen
  const hasSeenWelcome = localStorage.getItem('hasSeenWelcome') === 'true';

  // Determine current screen from URL
  const currentScreen = useMemo(() => {
    const path = location.pathname;
    if (path === '/welcome') return 'welcome';
    if (path === '/how-to-play') return 'howToPlay';
    if (path.startsWith('/game/')) return 'competition';
    if (path.startsWith('/league/')) return 'leagueLeaderboard';
    if (path.startsWith('/main') || path === '/') {
      // Show welcome screen on first load if user hasn't seen it
      if (!hasSeenWelcome && path === '/') {
        return 'welcome';
      }
      return 'main';
    }
    return hasSeenWelcome ? 'main' : 'welcome';
  }, [location.pathname, hasSeenWelcome]);

  const signInInitiatedRef = useRef(false);
  useEffect(() => {
    if (currentScreen === 'welcome' && !isAuthenticated && !signInInitiatedRef.current) {
      signInInitiatedRef.current = true;
      if (usesClerk) {
        navigate('/sign-in');
      } else {
        initiateOAuthLogin().catch(error => {
          console.error('[PickEmApp] OAuth login failed:', error);
          signInInitiatedRef.current = false;
        });
      }
    }
  }, [currentScreen, isAuthenticated, navigate, usesClerk]);

  // Get active tab from URL params or default to 'myPicks'. Full-navigation
  // tenants (pre-DKPLAT-347) land on the Lobby tab by default.
  const activeTab =
    params.tab || searchParams.get('tab') || (pickemFullNavigation ? 'lobby' : 'myPicks');

  // Smart landing for the root URL: send the user straight into a pickem
  // when they have no entries yet, or to My Picks when they already do.
  // Only fires for path === '/' once competitions have loaded — leaves
  // explicit URLs (e.g. /main/myPicks) alone.
  useEffect(() => {
    // Gate on isFetched (not !isLoading) — `isLoading` is also false while
    // the underlying query is *disabled* waiting for user context, which
    // would cause us to redirect to My Picks before the data ever arrives.
    //
    // Skip entirely for full-navigation tenants (pre-DKPLAT-347): they land on
    // the Lobby tab and navigate manually rather than being auto-routed.
    if (pickemFullNavigation) return;
    if (location.pathname !== '/' || !hasSeenWelcome || !isFetched) return;

    const playable = competitions
      .filter(c => c.state !== 'final')
      .sort((a, b) => {
        if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
        return a.gameDate.getTime() - b.gameDate.getTime();
      });

    const next = playable[0];
    if (next && !next.hasSelections) {
      navigate(`/game/${next.id}`, { replace: true });
    } else {
      navigate('/main/myPicks', { replace: true });
    }
  }, [location.pathname, hasSeenWelcome, isFetched, competitions, navigate, pickemFullNavigation]);

  // Get game ID and league info from URL params
  const currentGameId = params.gameId || '';
  const currentLeague = useMemo(() => {
    const leagueName = params.leagueName || '';
    const color = (searchParams.get('color') as 'orange' | 'blue') || 'orange';
    return { name: decodeURIComponent(leagueName), color };
  }, [params.leagueName, searchParams]);

  const handleCreateLeague = () => {
    setShowLeagueCreated(true);
  };

  const handleClearSelections = () => {
    setSelectedTeams({});
    setSelectedProps({});
    setShowClearSelections(false);
  };

  const handleGameAction = (gameId: string) => {
    navigate(`/game/${gameId}?from=${activeTab}`);
  };

  const handleViewLeaderboard = (gameId: string) => {
    navigate(`/game/${gameId}?mode=leaderboard&from=${activeTab}`);
  };

  const handleLeagueLeaderboard = (leagueName: string, leagueColor: 'orange' | 'blue') => {
    navigate(`/league/${encodeURIComponent(leagueName)}?color=${leagueColor}`);
  };

  const handleTabChange = (tab: string) => {
    navigate(`/main/${tab}`);
  };

  // Must run before any early returns — Rules of Hooks
  const tabHasGames = useMemo(() => {
    switch (activeTab) {
      case 'lobby':
        return competitions.some(g => g.state !== 'final');
      case 'myPicks':
        return competitions.some(g => g.hasSelections && g.state !== 'final');
      case 'recent': {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return competitions.some(g => g.state === 'final' && g.gameDate >= sevenDaysAgo);
      }
      default:
        return false;
    }
  }, [activeTab, competitions]);

  const showSportFilter =
    (activeTab === 'lobby' || activeTab === 'myPicks' || activeTab === 'recent') &&
    (isLoading || tabHasGames);

  if (currentScreen === 'leagueLeaderboard') {
    return (
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <LeagueLeaderboardView
          leagueName={currentLeague.name}
          leagueColor={currentLeague.color}
          onBack={() => navigate('/main/friends')}
        />
      </ScreenLayout>
    );
  }

  if (currentScreen === 'competition') {
    return (
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <ErrorBoundary resetKeys={[currentGameId]}>
          {/* Full-navigation tenants (e.g. SkyPicks) navigate entirely in-app, so
              back always returns to the originating in-app tab (Lobby by default).
              This must take priority: the in-app Lobby tab opens contests with
              ?from=lobby (from=${activeTab}), which would otherwise collide with the
              external-lobby branch below and bounce them to the canonical external
              /tenant/{code}/lobby URL they don't use (DKPLAT-461).
              Non-full-nav (DK): when the user came from the external Lobby front-end
              (?from=lobby), return there; otherwise step back in history. */}
          <CompetitionView
            gameId={currentGameId}
            onBack={
              pickemFullNavigation
                ? () => navigate(`/main/${searchParams.get('from') || 'lobby'}`)
                : searchParams.get('from') === 'lobby'
                  ? () => {
                      window.location.href = resolveLobbyReturnUrl(
                        searchParams,
                        'pickem_lobby_return_url'
                      );
                    }
                  : () => navigate(-1)
            }
          />
        </ErrorBoundary>
      </ScreenLayout>
    );
  }

  // Screen routing
  if (currentScreen === 'welcome') {
    // Clerk users are redirected to /sign-in; OAuth users are redirected to the provider.
    if (!isAuthenticated) {
      return (
        <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
          <LoadingSpinner className="min-h-screen" />
        </ScreenLayout>
      );
    }
    return (
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <WelcomeScreen
          onHowToPlay={() => navigate('/how-to-play')}
          onLetsPlay={() => {
            localStorage.setItem('hasSeenWelcome', 'true');
            navigate(pickemFullNavigation ? '/main/lobby' : '/');
          }}
        />
      </ScreenLayout>
    );
  }

  if (currentScreen === 'howToPlay') {
    return (
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <HowToPlayScreen
          onBack={() => navigate('/welcome')}
          onGotIt={() => {
            localStorage.setItem('hasSeenWelcome', 'true');
            navigate(pickemFullNavigation ? '/main/lobby' : '/');
          }}
        />
      </ScreenLayout>
    );
  }

  const gameListProps = {
    competitions,
    selectedSport,
    isLoading,
    showBodyAds,
    onGameAction: handleGameAction,
    onViewLeaderboard: handleViewLeaderboard,
    onMenuClick: (gameId: string) => setContestDetailsGameId(gameId),
  };

  // Main app content
  const renderContent = () => {
    switch (activeTab) {
      case 'lobby':
        return <LobbyTab {...gameListProps} />;

      case 'myPicks':
        return <MyPicksTab {...gameListProps} />;

      case 'recent':
        return <RecentTab {...gameListProps} />;

      case 'leaderboard':
        return (
          <LeaderboardTab
            competitions={competitions}
            isLoading={isLoading}
            pickemFullNavigation={pickemFullNavigation}
            currentUserId={_currentUser?.id}
          />
        );

      case 'friends':
        return (
          <FriendsTab
            onCreateLeague={handleCreateLeague}
            onViewLeagueLeaderboard={handleLeagueLeaderboard}
          />
        );

      default:
        return null;
    }
  };

  return (
    <AppHeaderStyleProvider
      value={{
        headerBgGradientStart: defaultHeaderStyle.headerBgGradientStart,
        headerBgGradientEnd: defaultHeaderStyle.headerBgGradientEnd,
        headerBgImageUrl: defaultHeaderStyle.headerBgImageUrl,
      }}
    >
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <div
          className={
            ENABLE_MOBILE_CONTAINER ? 'h-full overflow-y-auto' : 'min-h-screen flex flex-col'
          }
        >
          {/* Top-of-page ad placement */}
          <TopPageAd />

          {defaultHeaderStyle.isHeaderVisible && (
            <>
              <AppHeader
                headerLogoUrl={defaultHeaderStyle.headerLogoUrl}
                isLogoLoading={defaultHeaderStyle.isLoading}
                suppressFallbacks
              />

              {/* Below-header ad placement */}
              <BelowHeaderAd />
            </>
          )}

          <main className="px-6 py-8 flex-1 flex flex-col gap-6">
            <NavigationTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              fullNavigation={pickemFullNavigation}
            />

            {showSportFilter && (
              <SportFilter selectedSport={selectedSport} onSportChange={setSelectedSport} />
            )}

            <div key={activeTab}>{renderContent()}</div>
          </main>

          <AppFooter />
        </div>
      </ScreenLayout>

      {/* Other Modals */}
      <LeagueCreatedModal
        isOpen={showLeagueCreated}
        onClose={() => setShowLeagueCreated(false)}
        pin="8JK376"
        onViewLeague={() => {
          setShowLeagueCreated(false);
          navigate('/main/friends');
        }}
      />

      <PicksSubmittedModal
        isOpen={showPicksSubmitted}
        onClose={() => setShowPicksSubmitted(false)}
        potentialWinnings="$813.95"
        onAddToBetSlip={() => setShowPicksSubmitted(false)}
        onReturnToLobby={() => setShowPicksSubmitted(false)}
      />

      <ClearSelectionsModal
        isOpen={showClearSelections}
        onClose={() => setShowClearSelections(false)}
        onClearPicks={handleClearSelections}
      />

      {/* Contest Details Modal */}
      <ContestDetailsModal
        isOpen={!!contestDetailsGameId}
        onClose={() => setContestDetailsGameId(null)}
        pickemDetails={contestDetailsData}
        competitionData={contestDetailsCompetition}
      />

      {/* Debug panel for ad configuration (dev only) */}
      {/* <AdDebug /> */}
    </AppHeaderStyleProvider>
  );
};

export default PickEmApp;
